import React, { Fragment, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import useStore from "@/stores/index";
import { getMessagesForClassroomUsers } from "@/stores/chatStore";
import { User } from "@/js/utils/userUtils";
import { ClassroomOverviewDTO } from "@/stores/classroomStore_I";
import { ChatThread } from "../types";
import NoEntries from "./NoEntries";
import SearchInput from "./SearchInput";
import StudentRow from "./StudentRow";

export interface StudentWithChatInfo extends User {
	hasUnreadMessages: boolean;
	threadKey: string;
}

interface Props {
	classroomId: string;
	classroom: ClassroomOverviewDTO;
	students: StudentWithChatInfo[];
	chatBlocked: boolean;
	hasUnreadMessages: boolean | Record<string, unknown>;
	onSelect: (username: string, threadKey?: string) => void;
}

const StudentsList: React.FC<Props> = (props) => {
	const { t } = useTranslation();
	const [studentsList, setStudentsList] = useState<StudentWithChatInfo[]>([]);
	const [searchQuery, setSearchQuery] = useState<string>("");
	const classroomIdForChat = props.classroom?.name;

	// Use Zustand selectors for reactive subscriptions
	// @ts-expect-error - threads property exists but not in IChatStore type definition
	const threadList = useStore((state) => state.chat.threads.data);
	const unreadMessagesCount = useStore((state) => state.chat.unreadMessagesCount.data);

	const handleSearchQueryChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
		setSearchQuery(evt.target.value);
	};

	const handleClearSearchQuery = (): void => {
		setSearchQuery("");
	};

	useEffect(() => {
		// @ts-expect-error - Leaving code as it is
		useStore.getState().chat.fetchClassOverview({
			classroomId: classroomIdForChat,
		});

		if (props.students && props.students.length) {
			setStudentsList(props.students);

			// update the threadlist
			getMessagesForClassroomUsers(useStore.getState(), props.students, props.classroom);
		}
	}, [props.students, props.classroom, classroomIdForChat]);

	if (!studentsList.length) {
		return (
			<NoEntries title={t("classroomChat.noStudentsTitle")}>
				<p>
					<Trans
						i18nKey="classroomChat.noStudentsBody"
						components={{
							addStudentsLink: (
								<a href="https://docs.classwize.net/help/intro-classtools#adding-a-student" target="_blank" rel="noopener noreferrer" />
							),
						}}
					/>
				</p>
			</NoEntries>
		);
	}

	const getThreadKeyForStudent = (student: User): string => {
		// @ts-expect-error - Leaving code as it is
		return useStore.getState().chat.getThreadKey(props.classroom, student);
	};

	const getThread = (threadKey: string): ChatThread => {
		return threadList && threadList[threadKey] ? threadList[threadKey][0] : {};
	};

	const sortStudentListByOlderUnread = (studentList: StudentWithChatInfo[]): StudentWithChatInfo[] => {
		return studentList.sort((studentA, studentB) => {
			const threadA = getThread(studentA.threadKey || "");
			const threadB = getThread(studentB.threadKey || "");

			const dateA = new Date(threadA.timestamp || 0);
			const dateB = new Date(threadB.timestamp || 0);

			return dateA.getTime() < dateB.getTime() ? -1 : 1;
		});
	};

	const studentsWithUnreadMsgs: StudentWithChatInfo[] = [];
	const studentsWithReadMsgs: StudentWithChatInfo[] = [];
	const studentsWithReadMsgsNoFirstName: StudentWithChatInfo[] = [];

	studentsList
		.filter(
			({ username, fname, lname }) =>
				username.toLowerCase().includes(searchQuery.toLowerCase()) ||
				(fname && fname.toLowerCase().includes(searchQuery.toLowerCase())) ||
				(lname && lname.toLowerCase().includes(searchQuery.toLowerCase()))
		)
		.forEach((student) => {
			// parse students data with threadKey and unreadMsgCount and categorize into groups
			const threadKey = getThreadKeyForStudent(student);
			const hasUnreadMessages: boolean = unreadMessagesCount[threadKey] ? unreadMessagesCount[threadKey].hasUnreadMessages : false;
			student.hasUnreadMessages = hasUnreadMessages;
			student.threadKey = threadKey;

			if (hasUnreadMessages) {
				studentsWithUnreadMsgs.push(student);
				return;
			}

			if (!student.fname) {
				studentsWithReadMsgsNoFirstName.push(student);
				return;
			}

			studentsWithReadMsgs.push(student);
		});

	// sort students with unread messages with oldest unread messages
	sortStudentListByOlderUnread(studentsWithUnreadMsgs);

	// sort students with all read messages by first name in alphabetical order
	studentsWithReadMsgs.sort((studentA, studentB) =>
		sortByAlphabeticalOrder(studentA.fname?.toLowerCase() || "", studentB.fname?.toLowerCase() || "")
	);

	// sort students with all read messages but no first names by username in alphabetical order
	studentsWithReadMsgsNoFirstName.sort((studentA, studentB) =>
		sortByAlphabeticalOrder(studentA.username.toLowerCase(), studentB.username.toLowerCase())
	);

	const studentJsx = [...studentsWithUnreadMsgs, ...studentsWithReadMsgs, ...studentsWithReadMsgsNoFirstName].map((student) => (
		<Fragment key={student.username}>
			<StudentRow
				threadKey={student.threadKey}
				student={student}
				thread={getThread(student.threadKey || "")}
				onSelect={props.onSelect}
				hasUnreadMessages={student.hasUnreadMessages}
			/>
			<div className="ClassroomChat_separator" />
		</Fragment>
	));

	return (
		<Fragment>
			<SearchInput value={searchQuery} handleChange={handleSearchQueryChange} handleClear={handleClearSearchQuery} disabled={false} />
			{studentJsx}
			{!studentJsx.length && (
				<NoEntries title="No results found">
					<p>Try checking your spelling or using more general terms.</p>
				</NoEntries>
			)}
		</Fragment>
	);
};

export default StudentsList;

function sortByAlphabeticalOrder(a: string, b: string): number {
	return a.localeCompare(b, "en", { sensitivity: "base" });
}
